import { getMessagingIfSupported } from "../plugins/firebase";
import { getToken } from "firebase/messaging";
import { isIOS, isPWAInstalled } from "./deviceDetection";

/**
 * Service Worker内でトークン取得を試みる（iOS PWA対応）
 *
 * @param {ServiceWorkerRegistration} swRegistration Service Worker登録オブジェクト
 * @param {string} vapidKey VAPID公開鍵
 * @returns {Promise<string|null>} FCMトークン、または失敗時はnull
 */
const getTokenFromServiceWorker = (swRegistration, vapidKey) => {
  return new Promise((resolve, reject) => {
    if (!swRegistration || !swRegistration.active) {
      reject(new Error('Service Worker is not active'));
      return;
    }

    const messageChannel = new MessageChannel();

    messageChannel.port1.onmessage = (event) => {
      if (event.data.success) {
        resolve(event.data.token);
      } else {
        reject(new Error(event.data.error?.message || 'Service Worker getToken failed'));
      }
    };

    const timeoutId = setTimeout(() => {
      reject(new Error('Service Worker getToken timed out'));
    }, 30000);

    swRegistration.active.postMessage(
      {
        type: 'GET_TOKEN',
        vapidKey: vapidKey,
      },
      [messageChannel.port2]
    );

    const originalOnMessage = messageChannel.port1.onmessage;
    messageChannel.port1.onmessage = (event) => {
      clearTimeout(timeoutId);
      originalOnMessage(event);
    };
  });
};

/**
 * 通知許可をリクエストし、FCMトークンを取得する
 *
 * iOS PWA対応:
 * - タイムアウト時はService Worker経由でフォールバック
 * - 全環境で同じフロー（メインスレッド→フォールバック）
 *
 * @returns {Promise<string|null>} FCMトークン、または失敗時はnull
 */
export const requestPermissionAndGetToken = async () => {
  try {
    // Messagingが利用できない環境（非対応ブラウザ / Firebase初期化失敗）ではトークン取得をスキップ
    const messaging = await getMessagingIfSupported();

    if (messaging === null) {
      const initError = window.firebaseInitError;
      const reason = initError
        ? `Firebase ${initError.stage} failed: ${initError.message}`
        : 'Firebase messaging is not available';

      console.warn(`[FCM] Skip getting FCM token: ${reason}`);

      return null;
    }

    // 通知APIとService Workerのサポート確認
    if (!('Notification' in window)) {
      throw new Error('Notification API is not supported in this browser');
    }

    if (!('serviceWorker' in navigator)) {
      throw new Error('Service Worker is not supported in this browser');
    }

    // Service Worker登録状態を確認
    const registrations = await navigator.serviceWorker.getRegistrations();

    // firebase-messaging-sw.jsが登録されているか確認
    const firebaseSwFound = registrations.some(reg =>
      reg.active && reg.active.scriptURL.includes('firebase-messaging-sw.js')
    );

    if (!firebaseSwFound) {
      const registration = await navigator.serviceWorker.register('/firebase-messaging-sw.js', {
        scope: '/',
      });

      // アクティブになるまで待機
      if (registration.installing) {
        await new Promise((resolve) => {
          registration.installing.addEventListener('statechange', (e) => {
            if (e.target.state === 'activated') {
              resolve();
            }
          });
        });
      }
    }

    // VAPID Keyの存在確認
    const vapidKey = process.env.MIX_FIREBASE_CLOUD_MESSAGING_VAPID_KEY;
    if (!vapidKey) {
      throw new Error('VAPID key is not configured. Check MIX_FIREBASE_CLOUD_MESSAGING_VAPID_KEY environment variable');
    }

    // 通知許可をリクエスト
    if (Notification.permission !== 'granted') {
      const permission = await Notification.requestPermission();
      if (permission !== "granted") {
        throw new Error(`Notification permission denied. Current permission: ${permission}`);
      }
    }

    // Service Workerが完全に準備できているか確認
    const swReady = await navigator.serviceWorker.ready;

    // iOS PWA対応: Service Workerがactivatedになるまで追加で待機
    if (swReady.active && swReady.active.state !== 'activated') {
      await new Promise((resolve) => {
        swReady.active.addEventListener('statechange', function handler(e) {
          if (e.target.state === 'activated') {
            swReady.active.removeEventListener('statechange', handler);
            resolve();
          }
        });
      });
    }

    // iOS PWA対応: 追加で1秒待機してService Workerが完全に準備できるようにする
    await new Promise(resolve => setTimeout(resolve, 1000));

    // getToken()実行（タイムアウト付き）
    // 全ての環境で同じフロー（メインスレッド→フォールバック）
    let timeoutId;
    const token = await Promise.race([
      (async () => {
        try {
          const result = await getToken(messaging, {
            vapidKey: vapidKey,
            serviceWorkerRegistration: swReady,
          });

          clearTimeout(timeoutId);
          return result;
        } catch (error) {
          clearTimeout(timeoutId);
          throw error;
        }
      })(),
      new Promise((_, reject) => {
        timeoutId = setTimeout(() => {
          reject(new Error('FCM token acquisition timed out after 10 seconds'));
        }, 10000);
      })
    ]).catch(async (error) => {
      // タイムアウトした場合、Service Worker内でのトークン取得を試みる
      try {
        const swToken = await getTokenFromServiceWorker(swReady, vapidKey);
        if (swToken) {
          return swToken;
        }
      } catch (swError) {
        // Service Workerでも失敗した場合は何もしない
      }

      // 両方失敗した場合は元のエラーをthrow
      throw error;
    });

    if (!token) {
      throw new Error('FCM token is null. getToken() returned no token');
    }

    return token;
  } catch (error) {
    console.error('[FCM] Error in requestPermissionAndGetToken:', error);
    throw error;
  }
}
